// apps/web/src/pages/GeofencesPage.tsx
//
// Geofence management dashboard. Operators draw polygons on a Google Map and
// configure DANGER / SAFE / RESTRICTED / MONITORED zones. Mobile clients
// receive push notifications when they enter DANGER zones within active hours.
//
// Data flow:
//   - List:   RPC `geofences_list_geojson` returns rows with polygon as GeoJSON
//   - Save:   RPC `geofence_save(p_id, ..., p_polygon_geojson)` upserts
//   - Delete: direct DELETE against `geofences` table (RLS enforced)
//
// Polygons are rendered through native `google.maps.Polygon` instances created
// imperatively because @vis.gl/react-google-maps does not ship a <Polygon>
// component. The drawing tool also uses the native DrawingManager.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { GEOFENCE_TYPE_LABELS, LOCATION_CONFIG } from '@asi/shared';
import type { GeofenceType, GeofenceSeverity } from '@asi/shared';
import { Plus, Trash2, Save, X, Pencil, Hexagon, MousePointerClick, Mountain, Map as MapTypeIcon, Layers, Box } from 'lucide-react';
import { supabase } from '@/services/supabase';
import { usePermissions } from '@/hooks/usePermissions';
import { PlaceSearchInput } from '@/components/PlaceSearchInput';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
const MAP_ID = 'geofences-map';

const TYPE_COLORS: Record<GeofenceType, string> = {
  SAFE: '#16A34A',
  DANGER: '#DC2626',
  RESTRICTED: '#EAB308',
  MONITORED: '#2563EB',
};

const SEVERITY_OPTIONS: GeofenceSeverity[] = ['low', 'medium', 'high', 'critical'];
const SEVERITY_LABELS: Record<GeofenceSeverity, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
};

interface GeofenceRow {
  id: string;
  name: string;
  description: string | null;
  type: GeofenceType;
  alert_on_entry: boolean;
  alert_on_exit: boolean;
  is_active: boolean;
  severity: GeofenceSeverity | null;
  proximity_radius_m: number | null;
  notification_title: string | null;
  notification_body: string | null;
  category: string | null;
  active_hours_start: string | null;
  active_hours_end: string | null;
  active_hours_tz: string | null;
  polygon_geojson: { type: 'Polygon'; coordinates: number[][][] };
  created_at: string;
  updated_at: string;
}

interface DraftGeofence {
  id: string | null;
  name: string;
  description: string;
  type: GeofenceType;
  category: string;
  severity: GeofenceSeverity;
  active_hours_start: string; // HH:MM
  active_hours_end: string;   // HH:MM
  alert_on_entry: boolean;
  alert_on_exit: boolean;
  proximity_radius_m: number;
  notification_title: string;
  notification_body: string;
  is_active: boolean;
}

const EMPTY_DRAFT: DraftGeofence = {
  id: null,
  name: '',
  description: '',
  type: 'DANGER',
  category: '',
  severity: 'medium',
  active_hours_start: '',
  active_hours_end: '',
  alert_on_entry: true,
  alert_on_exit: false,
  proximity_radius_m: 200,
  notification_title: '',
  notification_body: '',
  is_active: true,
};

function trimTime(t: string | null | undefined): string {
  return t && t.length >= 5 ? t.slice(0, 5) : '';
}

function isValidHHMM(s: string): boolean {
  return /^\d{2}:\d{2}$/.test(s) && parseInt(s.slice(0, 2), 10) < 24 && parseInt(s.slice(3, 5), 10) < 60;
}

/** Convert google.maps.LatLng[] ring (no closing point) to GeoJSON Polygon. */
function pathToGeoJSON(path: google.maps.LatLng[]): { type: 'Polygon'; coordinates: number[][][] } {
  const ring = path.map((p) => [p.lng(), p.lat()]);
  if (ring.length > 0) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

function draftFromRow(row: GeofenceRow): DraftGeofence {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    type: row.type,
    category: row.category ?? '',
    severity: (row.severity as GeofenceSeverity) ?? 'medium',
    active_hours_start: trimTime(row.active_hours_start),
    active_hours_end: trimTime(row.active_hours_end),
    alert_on_entry: row.alert_on_entry,
    alert_on_exit: row.alert_on_exit,
    proximity_radius_m: row.proximity_radius_m ?? 200,
    notification_title: row.notification_title ?? '',
    notification_body: row.notification_body ?? '',
    is_active: row.is_active,
  };
}

export function GeofencesPage() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <GeofencesPageInner />
    </APIProvider>
  );
}

function GeofencesPageInner() {
  const { can } = usePermissions();
  const [geofences, setGeofences] = useState<GeofenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftGeofence | null>(null);
  const [pendingPath, setPendingPath] = useState<google.maps.LatLng[] | null>(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Quick preview when an operator clicks a polygon (read-only highlight + InfoWindow).
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Live reference to the editable polygon currently on the map (if any).
  // Used at save-time so vertex/shape drags don't trigger React re-renders mid-drag.
  const editPolygonRef = useRef<google.maps.Polygon | null>(null);
  // Refs to sidebar items so we can scrollIntoView the previewed/selected one.
  const sidebarItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const fetchGeofences = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase.rpc('geofences_list_geojson');
    if (err) {
      console.error('[Geofences] list failed', err);
      setError(err.message);
    } else {
      const parsed = ((data as unknown[]) ?? []).map((r) => {
        const row = r as GeofenceRow & { polygon_geojson: string | object };
        return {
          ...row,
          polygon_geojson:
            typeof row.polygon_geojson === 'string' ? JSON.parse(row.polygon_geojson) : row.polygon_geojson,
        } as GeofenceRow;
      });
      setGeofences(parsed);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGeofences(); }, [fetchGeofences]);

  const startNew = useCallback(() => {
    setDraft({ ...EMPTY_DRAFT });
    setPendingPath(null);
    setDrawingMode(true);
  }, []);

  const startEdit = useCallback((row: GeofenceRow) => {
    setDraft(draftFromRow(row));
    setPendingPath(null);
    setDrawingMode(false);
  }, []);

  const cancelDraft = useCallback(() => {
    setDraft(null);
    setPendingPath(null);
    setDrawingMode(false);
  }, []);

  const handlePolygonDrawn = useCallback((path: google.maps.LatLng[]) => {
    setPendingPath(path);
    setDrawingMode(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.name.trim()) { setError('El nombre es obligatorio.'); return; }
    // Priority: freshly drawn path > live edits on existing polygon > stored geometry.
    const existing = draft.id ? geofences.find((g) => g.id === draft.id) : null;
    const editedPath = editPolygonRef.current?.getPath().getArray();
    const polygonGeoJSON = pendingPath
      ? pathToGeoJSON(pendingPath)
      : editedPath && editedPath.length >= 3
        ? pathToGeoJSON(editedPath)
        : existing?.polygon_geojson;
    if (!polygonGeoJSON) {
      setError('Dibuja el polígono en el mapa antes de guardar.');
      return;
    }
    const start = draft.active_hours_start.trim();
    const end = draft.active_hours_end.trim();
    if ((start || end) && !(isValidHHMM(start) && isValidHHMM(end))) {
      setError('Horario inválido. Usa HH:MM (00:00 – 23:59) o deja ambos vacíos.');
      return;
    }
    setSaving(true);
    setError(null);
    const { data: savedId, error: err } = await supabase.rpc('geofence_save', {
      p_id: draft.id,
      p_name: draft.name.trim(),
      p_description: draft.description.trim() || null,
      p_type: draft.type,
      p_alert_on_entry: draft.alert_on_entry,
      p_alert_on_exit: draft.alert_on_exit,
      p_is_active: draft.is_active,
      p_severity: draft.severity,
      p_proximity_radius_m: draft.proximity_radius_m,
      p_notification_title: draft.notification_title.trim() || null,
      p_notification_body: draft.notification_body.trim() || null,
      p_category: draft.category.trim() || null,
      p_active_hours_start: start ? `${start}:00` : null,
      p_active_hours_end: end ? `${end}:00` : null,
      p_active_hours_tz: 'America/Guatemala',
      p_polygon_geojson: JSON.stringify(polygonGeoJSON),
    });
    setSaving(false);
    if (err) {
      console.error('[Geofences] save failed', err);
      setError(err.message);
      return;
    }
    // Optimistic update: patch the in-memory list immediately so the polygon
    // re-renders without waiting for the round-trip RPC fetch.
    const newId = (typeof savedId === 'string' ? savedId : draft.id) ?? null;
    if (newId) {
      setGeofences((prev) => {
        const nowIso = new Date().toISOString();
        const merged: GeofenceRow = {
          id: newId,
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          type: draft.type,
          alert_on_entry: draft.alert_on_entry,
          alert_on_exit: draft.alert_on_exit,
          is_active: draft.is_active,
          severity: draft.severity,
          proximity_radius_m: draft.proximity_radius_m,
          notification_title: draft.notification_title.trim() || null,
          notification_body: draft.notification_body.trim() || null,
          category: draft.category.trim() || null,
          active_hours_start: start ? `${start}:00` : null,
          active_hours_end: end ? `${end}:00` : null,
          active_hours_tz: 'America/Guatemala',
          polygon_geojson: polygonGeoJSON,
          created_at: prev.find((g) => g.id === newId)?.created_at ?? nowIso,
          updated_at: nowIso,
        };
        const idx = prev.findIndex((g) => g.id === newId);
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = merged;
          return next;
        }
        return [...prev, merged];
      });
    }
    setDraft(null);
    setPendingPath(null);
    // Background refresh to reconcile with server-side fields (timestamps, etc.).
    fetchGeofences();
  }, [draft, pendingPath, geofences, fetchGeofences]);

  const handleDelete = useCallback(async (row: GeofenceRow) => {
    if (!window.confirm(`¿Eliminar la geocerca "${row.name}"?`)) return;
    const { error: err } = await supabase.from('geofences').delete().eq('id', row.id);
    if (err) {
      setError(err.message);
      return;
    }
    if (draft?.id === row.id) setDraft(null);
    fetchGeofences();
  }, [draft, fetchGeofences]);

  const initialPath = useMemo(() => {
    if (!draft?.id) return null;
    const row = geofences.find((g) => g.id === draft.id);
    if (!row) return null;
    return row.polygon_geojson.coordinates[0] ?? null;
  }, [draft, geofences]);

  // Scroll the previewed item into view in the sidebar.
  useEffect(() => {
    if (!previewId) return;
    const el = sidebarItemRefs.current[previewId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [previewId]);

  // Clear preview if we enter edit/draw mode (the form takes over).
  useEffect(() => {
    if (draft || drawingMode) setPreviewId(null);
  }, [draft, drawingMode]);

  const handlePolygonClick = useCallback((id: string) => setPreviewId(id), []);

  const previewRow = useMemo(
    () => (previewId ? geofences.find((g) => g.id === previewId) ?? null : null),
    [previewId, geofences],
  );

  // Centroid of the previewed polygon — drives the fly-to animation.
  const previewCenter = useMemo(() => {
    if (!previewRow) return null;
    return polygonCentroid(previewRow);
  }, [previewRow]);

  return (
    <div className="flex h-full">
      <div className="flex-1 relative">
        <div className="absolute inset-0">
          <Map
            id={MAP_ID}
            defaultCenter={LOCATION_CONFIG.GUATEMALA_CENTER}
            defaultZoom={12}
            gestureHandling="greedy"
            disableDefaultUI={true}
            zoomControl={true}
            scaleControl={false}
            streetViewControl={true}
            streetViewControlOptions={{ position: 9 as google.maps.ControlPosition /* RIGHT_BOTTOM */ }}
            fullscreenControl={false}
            mapTypeControl={false}
            rotateControl={false}
            onClick={() => setPreviewId(null)}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div className="absolute top-3 left-3 z-10 w-80">
          <StreetViewHide mapId={MAP_ID}>
            <PlaceSearchInput mapId={MAP_ID} onPlaceSelect={() => {}} />
          </StreetViewHide>
        </div>

        <MapOverlay
          mapId={MAP_ID}
          geofences={geofences}
          selectedId={draft?.id ?? null}
          previewId={previewId}
          drawingMode={drawingMode}
          onPolygonDrawn={handlePolygonDrawn}
          onPolygonClick={handlePolygonClick}
          initialEditPath={pendingPath ? pendingPath.map((p) => [p.lng(), p.lat()]) : initialPath}
          editing={!!draft}
          editPolygonRef={editPolygonRef}
        />

        {previewRow && !draft && !drawingMode && (
          <GeofencePreview
            mapId={MAP_ID}
            row={previewRow}
            onClose={() => setPreviewId(null)}
            onEdit={can('geofences.manage') ? () => { startEdit(previewRow); setPreviewId(null); } : undefined}
          />
        )}

        <StreetViewHide mapId={MAP_ID}>
          <MapViewControls mapId={MAP_ID} />
        </StreetViewHide>

        <GeofenceFlyToController mapId={MAP_ID} target={previewCenter} />

        <StreetViewHide mapId={MAP_ID}>
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-2 items-end">
            {!draft && can('geofences.manage') && (
              <button
                onClick={startNew}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 py-2 text-sm font-medium flex items-center gap-1.5 shadow-md"
              >
                <Plus className="w-4 h-4" /> Nueva geocerca
              </button>
            )}
            {drawingMode && (
              <div className="bg-black/80 text-white rounded-md px-3 py-2 text-xs max-w-xs text-right shadow-md flex items-start gap-2">
                <MousePointerClick className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Haz clic en el mapa para añadir vértices y <b>doble clic</b> en el último para cerrar el polígono.</span>
              </div>
            )}
            {!drawingMode && draft && initialPath && (
              <div className="bg-amber-500/95 text-white rounded-md px-3 py-2 text-xs max-w-xs text-right shadow-md">
                Arrastra los vértices o el polígono para ajustarlo.
              </div>
            )}
            {pendingPath && draft && !drawingMode && (
              <div className="bg-emerald-600 text-white rounded-md px-3 py-2 text-xs shadow-md">
                Polígono actualizado ({pendingPath.length} vértices).
              </div>
            )}
          </div>
        </StreetViewHide>
      </div>

      <div className="w-96 border-l border-border bg-card flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Geocercas</h3>
          <p className="text-muted-foreground text-sm">{geofences.length} zonas registradas</p>
        </div>

        {draft ? (
          <DraftForm
            draft={draft}
            setDraft={setDraft}
            onCancel={cancelDraft}
            onSave={handleSave}
            onRedraw={() => { setPendingPath(null); setDrawingMode(true); }}
            saving={saving}
            error={error}
            hasPath={!!pendingPath || !!initialPath}
          />
        ) : (
          <div className="flex-1 overflow-auto p-2 space-y-1">
            {loading && <p className="text-muted-foreground text-sm p-2">Cargando…</p>}
            {!loading && geofences.length === 0 && (
              <p className="text-muted-foreground/60 text-center py-8 text-sm">
                No hay geocercas. Crea la primera con el botón “Nueva geocerca”.
              </p>
            )}
            {geofences.map((g) => {
              const isPreviewed = g.id === previewId;
              return (
                <div
                  key={g.id}
                  ref={(el) => { sidebarItemRefs.current[g.id] = el; }}
                  className={`p-3 rounded-lg cursor-pointer group transition-colors ${
                    isPreviewed
                      ? 'bg-primary/10 ring-1 ring-primary/40'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setPreviewId(g.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                      className="w-3 h-3 rounded shrink-0"
                      style={{ backgroundColor: TYPE_COLORS[g.type] }}
                    />
                    <span className="text-foreground text-sm font-medium truncate">{g.name}</span>
                  </div>
                  {can('geofences.manage') && (
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); startEdit(g); }}
                        title="Editar"
                        className="text-muted-foreground hover:text-foreground p-1"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(g); }}
                        title="Eliminar"
                        className="text-red-500 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <span>{GEOFENCE_TYPE_LABELS[g.type]}</span>
                  {g.category && <span>· {g.category}</span>}
                  {g.severity && <span>· {SEVERITY_LABELS[g.severity]}</span>}
                  {g.active_hours_start && g.active_hours_end && (
                    <span>· {trimTime(g.active_hours_start)}–{trimTime(g.active_hours_end)}</span>
                  )}
                  {!g.is_active && <span className="text-red-500">· Inactiva</span>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Map overlay: draws polygons + DrawingManager imperatively.
// ────────────────────────────────────────────────────────────────────────────

interface MapOverlayProps {
  mapId: string;
  geofences: GeofenceRow[];
  selectedId: string | null;
  previewId: string | null;
  drawingMode: boolean;
  onPolygonDrawn: (path: google.maps.LatLng[]) => void;
  onPolygonClick: (id: string) => void;
  initialEditPath: number[][] | null;
  editing: boolean;
  editPolygonRef: React.MutableRefObject<google.maps.Polygon | null>;
}

function MapOverlay(props: MapOverlayProps) {
  const map = useMap(props.mapId);
  const polygonsRef = useRef<Map<string, google.maps.Polygon> | null>(null);
  if (polygonsRef.current === null) polygonsRef.current = new globalThis.Map();
  const polygonClickListenersRef = useRef<Map<string, google.maps.MapsEventListener> | null>(null);
  if (polygonClickListenersRef.current === null) polygonClickListenersRef.current = new globalThis.Map();
  const editPolygonRef = props.editPolygonRef;
  // Keep the latest callbacks reachable from listener closures.
  const onPolygonDrawnRef = useRef(props.onPolygonDrawn);
  useEffect(() => { onPolygonDrawnRef.current = props.onPolygonDrawn; }, [props.onPolygonDrawn]);
  const onPolygonClickRef = useRef(props.onPolygonClick);
  useEffect(() => { onPolygonClickRef.current = props.onPolygonClick; }, [props.onPolygonClick]);

  // Manual polygon drawing state (replaces deprecated DrawingManager).
  const drawingPointsRef = useRef<google.maps.LatLng[]>([]);
  const drawingMarkersRef = useRef<google.maps.Marker[]>([]);
  const drawingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const drawingPreviewLineRef = useRef<google.maps.Polyline | null>(null);
  const drawingClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const drawingDblClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const drawingMouseMoveListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Render persisted polygons.
  useEffect(() => {
    if (!map) return;
    const current = polygonsRef.current!;
    const listeners = polygonClickListenersRef.current!;
    const keepIds = new Set(props.geofences.map((g) => g.id));
    // Remove stale.
    for (const [id, poly] of current.entries()) {
      if (!keepIds.has(id) || id === props.selectedId) {
        listeners.get(id)?.remove();
        listeners.delete(id);
        poly.setMap(null);
        current.delete(id);
      }
    }
    // Add/refresh.
    for (const g of props.geofences) {
      if (g.id === props.selectedId) continue; // shown as editable instead
      const color = TYPE_COLORS[g.type];
      const isPreview = g.id === props.previewId;
      if (current.has(g.id)) {
        // Update visual to reflect preview state without rebuilding.
        current.get(g.id)!.setOptions({
          strokeWeight: isPreview ? 4 : 2,
          strokeOpacity: isPreview ? 1 : 0.85,
          fillOpacity: isPreview ? 0.35 : (g.is_active ? 0.18 : 0.06),
          zIndex: isPreview ? 1000 : 1,
        });
        continue;
      }
      const ring = g.polygon_geojson.coordinates[0] ?? [];
      const path = ring.map(([lng, lat]) => ({ lat, lng }));
      const poly = new google.maps.Polygon({
        paths: path,
        strokeColor: color,
        strokeOpacity: isPreview ? 1 : 0.85,
        strokeWeight: isPreview ? 4 : 2,
        fillColor: color,
        fillOpacity: isPreview ? 0.35 : (g.is_active ? 0.18 : 0.06),
        clickable: true,
        zIndex: isPreview ? 1000 : 1,
        map,
      });
      current.set(g.id, poly);
      const id = g.id;
      listeners.set(
        id,
        google.maps.event.addListener(poly, 'click', () => onPolygonClickRef.current(id)),
      );
    }
  }, [map, props.geofences, props.selectedId, props.previewId]);

  // Helper: clear all drawing UI overlays.
  const clearDrawingOverlays = useCallback(() => {
    drawingMarkersRef.current.forEach((m) => m.setMap(null));
    drawingMarkersRef.current = [];
    drawingPolylineRef.current?.setMap(null);
    drawingPolylineRef.current = null;
    drawingPreviewLineRef.current?.setMap(null);
    drawingPreviewLineRef.current = null;
    drawingPointsRef.current = [];
  }, []);

  // Cleanup all polygons on unmount.
  useEffect(() => {
    return () => {
      polygonClickListenersRef.current?.forEach((l) => l.remove());
      polygonClickListenersRef.current?.clear();
      polygonsRef.current?.forEach((p) => p.setMap(null));
      polygonsRef.current?.clear();
      editPolygonRef.current?.setMap(null);
      editPolygonRef.current = null;
      drawingClickListenerRef.current?.remove();
      drawingDblClickListenerRef.current?.remove();
      drawingMouseMoveListenerRef.current?.remove();
      clearDrawingOverlays();
    };
  }, [clearDrawingOverlays]);

  // Manual drawing tool: click to add vertices, double-click to close polygon.
  useEffect(() => {
    if (!map) return;
    // Remove previous listeners.
    drawingClickListenerRef.current?.remove();
    drawingDblClickListenerRef.current?.remove();
    drawingMouseMoveListenerRef.current?.remove();
    drawingClickListenerRef.current = null;
    drawingDblClickListenerRef.current = null;
    drawingMouseMoveListenerRef.current = null;

    if (!props.drawingMode) {
      clearDrawingOverlays();
      map.setOptions({ draggableCursor: null, draggingCursor: null });
      return;
    }

    map.setOptions({ draggableCursor: 'crosshair', draggingCursor: 'crosshair' });
    clearDrawingOverlays();

    // Polyline showing drawn path so far.
    // clickable: false is required so that clicks/dblclicks on the line pass through to the map.
    drawingPolylineRef.current = new google.maps.Polyline({
      strokeColor: TYPE_COLORS.DANGER,
      strokeWeight: 2,
      strokeOpacity: 0.9,
      clickable: false,
      map,
    });
    // Preview line from last point to mouse cursor.
    drawingPreviewLineRef.current = new google.maps.Polyline({
      strokeColor: TYPE_COLORS.DANGER,
      strokeWeight: 1.5,
      strokeOpacity: 0.5,
      clickable: false,
      map,
    });

    drawingClickListenerRef.current = google.maps.event.addListener(
      map,
      'click',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        const pts = drawingPointsRef.current;
        pts.push(e.latLng);
        // Update drawn polyline.
        drawingPolylineRef.current?.setPath(pts);
        // Add vertex marker.
        const marker = new google.maps.Marker({
          position: e.latLng,
          map,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: '#DC2626', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5 },
          clickable: false,
          zIndex: 10,
        });
        drawingMarkersRef.current.push(marker);
      },
    );

    drawingDblClickListenerRef.current = google.maps.event.addListener(
      map,
      'dblclick',
      (e: google.maps.MapMouseEvent) => {
        e.stop?.();
        const pts = drawingPointsRef.current;
        // Google Maps fires exactly 2 'click' events before every 'dblclick'.
        // Those 2 ghost clicks were already added to drawingPointsRef, so we strip them.
        const ghostCount = 2;
        if (pts.length < 3 + ghostCount) return;
        const completed = pts.slice(0, pts.length - ghostCount);
        clearDrawingOverlays();
        map.setOptions({ draggableCursor: null, draggingCursor: null });
        drawingClickListenerRef.current?.remove();
        drawingDblClickListenerRef.current?.remove();
        drawingMouseMoveListenerRef.current?.remove();
        onPolygonDrawnRef.current(completed);
      },
    );

    drawingMouseMoveListenerRef.current = google.maps.event.addListener(
      map,
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        const pts = drawingPointsRef.current;
        if (!e.latLng || pts.length === 0) return;
        drawingPreviewLineRef.current?.setPath([pts[pts.length - 1], e.latLng]);
      },
    );
  }, [map, props.drawingMode, clearDrawingOverlays]);

  // Editable polygon for current draft. Operators can drag vertices or the whole shape.
  // We deliberately do NOT bubble path edits back to React state — the live polygon
  // instance is exposed through `editPolygonRef`, and the parent reads it at save time.
  // This keeps drag interactions smooth (no re-render mid-drag).
  useEffect(() => {
    if (!map) return;
    if (editPolygonRef.current) {
      editPolygonRef.current.setMap(null);
      editPolygonRef.current = null;
    }
    if (!props.editing || !props.initialEditPath || props.drawingMode) return;
    const path = props.initialEditPath.map(([lng, lat]) => ({ lat, lng }));
    const poly = new google.maps.Polygon({
      paths: path,
      strokeColor: '#F59E0B',
      strokeOpacity: 0.95,
      strokeWeight: 2,
      fillColor: '#F59E0B',
      fillOpacity: 0.2,
      editable: true,
      draggable: true,
      map,
    });
    editPolygonRef.current = poly;

    // Pan to the centroid.
    const bounds = new google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    if (!bounds.isEmpty()) map.fitBounds(bounds, 60);
  }, [map, props.editing, props.initialEditPath, props.drawingMode, editPolygonRef]);

  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Custom map-type + 3D toggle control (bottom-right, above zoom).
// Replaces Google's hard-to-read mapTypeControl with our themed pills.
// ────────────────────────────────────────────────────────────────────────────

type MapTypeKey = 'roadmap' | 'satellite' | 'hybrid' | 'terrain';

const MAP_TYPE_BUTTONS: { key: MapTypeKey; label: string; icon: typeof MapTypeIcon }[] = [
  { key: 'roadmap', label: 'Mapa', icon: MapTypeIcon },
  { key: 'satellite', label: 'Satélite', icon: Layers },
  { key: 'hybrid', label: 'Híbrido', icon: Layers },
  { key: 'terrain', label: 'Relieve', icon: Mountain },
];

function MapViewControls({ mapId }: { mapId: string }) {
  const map = useMap(mapId);
  const [mapType, setMapType] = useState<MapTypeKey>('roadmap');
  const [tilted, setTilted] = useState(false);

  useEffect(() => {
    if (!map) return;
    const sync = () => {
      const t = (map.getMapTypeId() ?? 'roadmap') as MapTypeKey;
      setMapType(t);
      const tilt = map.getTilt() ?? 0;
      const heading = map.getHeading() ?? 0;
      setTilted(tilt > 0.5 || Math.abs(heading) > 0.5);
    };
    sync();
    const ls = [
      map.addListener('maptypeid_changed', sync),
      map.addListener('tilt_changed', sync),
      map.addListener('heading_changed', sync),
    ];
    return () => ls.forEach((l) => l.remove());
  }, [map]);

  if (!map) return null;

  const enter3D = () => {
    // Tilt the current view without forcing a map-type change. Tilt + buildings
    // render best on satellite/hybrid at high zoom, but we let the operator
    // choose with the type pills.
    if ((map.getZoom() ?? 12) < 17) map.setZoom(17);
    map.setTilt(45);
  };

  const exit3D = () => {
    map.setTilt(0);
    map.setHeading(0);
  };

  return (
    <div className="absolute bottom-3 right-16 z-10 flex flex-col items-end gap-2">
      <div className="flex bg-card/95 border border-border rounded-md shadow-md overflow-hidden text-xs">
        {MAP_TYPE_BUTTONS.map((b) => {
          const active = mapType === b.key;
          const Icon = b.icon;
          return (
            <button
              key={b.key}
              onClick={() => map.setMapTypeId(b.key)}
              className={`px-2.5 py-1.5 flex items-center gap-1.5 font-medium border-l border-border first:border-l-0 transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
              title={b.label}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{b.label}</span>
            </button>
          );
        })}
      </div>

      {tilted ? (
        <button
          onClick={exit3D}
          className="bg-card/95 border border-border rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md hover:bg-muted text-foreground flex items-center gap-1.5"
          title="Volver a vista 2D"
        >
          <MapTypeIcon className="w-3.5 h-3.5" />
          Vista 2D
        </button>
      ) : (
        <button
          onClick={enter3D}
          className="bg-card/95 border border-border rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md hover:bg-muted text-foreground flex items-center gap-1.5"
          title="Vista 3D (zoom alto + satélite)"
        >
          <Box className="w-3.5 h-3.5" />
          Vista 3D
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sidebar form.
// ────────────────────────────────────────────────────────────────────────────

interface DraftFormProps {
  draft: DraftGeofence;
  setDraft: React.Dispatch<React.SetStateAction<DraftGeofence | null>>;
  onCancel: () => void;
  onSave: () => void;
  onRedraw: () => void;
  saving: boolean;
  error: string | null;
  hasPath: boolean;
}

function DraftForm({ draft, setDraft, onCancel, onSave, onRedraw, saving, error, hasPath }: DraftFormProps) {
  const patch = useCallback(
    (k: keyof DraftGeofence, v: unknown) => setDraft((d) => (d ? { ...d, [k]: v } : d)),
    [setDraft],
  );

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-foreground">
            {draft.id ? 'Editar geocerca' : 'Nueva geocerca'}
          </h4>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Field label="Nombre *">
          <input
            value={draft.name}
            onChange={(e) => patch('name', e.target.value)}
            className={inputCls}
            placeholder="Ej. Zona Roja Zona 18"
          />
        </Field>

        <Field label="Descripción">
          <textarea
            value={draft.description}
            onChange={(e) => patch('description', e.target.value)}
            className={`${inputCls} min-h-[60px] resize-y`}
            placeholder="Notas internas (opcional)"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Tipo">
            <select
              value={draft.type}
              onChange={(e) => patch('type', e.target.value as GeofenceType)}
              className={inputCls}
            >
              {(Object.keys(GEOFENCE_TYPE_LABELS) as GeofenceType[]).map((t) => (
                <option key={t} value={t}>{GEOFENCE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <Field label="Severidad">
            <select
              value={draft.severity}
              onChange={(e) => patch('severity', e.target.value as GeofenceSeverity)}
              className={inputCls}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Categoría (etiqueta libre)">
          <input
            value={draft.category}
            onChange={(e) => patch('category', e.target.value)}
            className={inputCls}
            placeholder="robos, extorsiones, narco…"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Hora inicio (HH:MM)">
            <input
              value={draft.active_hours_start}
              onChange={(e) => patch('active_hours_start', e.target.value)}
              className={inputCls}
              placeholder="18:00"
            />
          </Field>
          <Field label="Hora fin (HH:MM)">
            <input
              value={draft.active_hours_end}
              onChange={(e) => patch('active_hours_end', e.target.value)}
              className={inputCls}
              placeholder="05:00"
            />
          </Field>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Deja ambos vacíos para mantener la zona activa 24/7. Zona horaria: America/Guatemala.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Alerta al entrar">
            <ToggleRow
              value={draft.alert_on_entry}
              onChange={(v) => patch('alert_on_entry', v)}
              label={draft.alert_on_entry ? 'Sí' : 'No'}
            />
          </Field>
          <Field label="Alerta al salir">
            <ToggleRow
              value={draft.alert_on_exit}
              onChange={(v) => patch('alert_on_exit', v)}
              label={draft.alert_on_exit ? 'Sí' : 'No'}
            />
          </Field>
        </div>

        <Field label="Radio de proximidad (m)">
          <input
            type="number"
            min={50}
            max={5000}
            step={50}
            value={draft.proximity_radius_m}
            onChange={(e) => patch('proximity_radius_m', Math.max(50, Math.min(5000, parseInt(e.target.value || '200', 10))))}
            className={inputCls}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Distancia para activar el geofence antes del polígono.
          </p>
        </Field>

        <Field label="Título de notificación (opcional)">
          <input
            value={draft.notification_title}
            onChange={(e) => patch('notification_title', e.target.value)}
            className={inputCls}
            placeholder="⚠️ Zona insegura"
          />
        </Field>

        <Field label="Mensaje de notificación (opcional)">
          <textarea
            value={draft.notification_body}
            onChange={(e) => patch('notification_body', e.target.value)}
            className={`${inputCls} min-h-[60px] resize-y`}
            placeholder="Estás cerca de una zona con incidentes reportados. Mantente alerta."
          />
        </Field>

        <Field label="Estado">
          <ToggleRow
            value={draft.is_active}
            onChange={(v) => patch('is_active', v)}
            label={draft.is_active ? 'Activa' : 'Inactiva'}
          />
        </Field>

        <div className="pt-2">
          <button
            onClick={onRedraw}
            className="w-full text-sm border border-border rounded-md py-2 hover:bg-muted text-foreground flex items-center justify-center gap-1.5"
          >
            <Hexagon className="w-4 h-4" />
            {hasPath ? 'Volver a dibujar polígono' : 'Dibujar polígono en el mapa'}
          </button>
          {hasPath && (
            <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
              También puedes arrastrar los vértices o el polígono directamente en el mapa.
            </p>
          )}
        </div>

        {error && (
          <div className="text-xs text-red-500 bg-red-500/10 rounded p-2">{error}</div>
        )}
      </div>

      <div className="border-t border-border p-3 flex gap-2 sticky bottom-0 bg-card">
        <button
          onClick={onCancel}
          className="flex-1 text-sm border border-border rounded-md py-2 hover:bg-muted text-foreground"
        >
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex-1 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md py-2 flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

const inputCls =
  'w-full text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ToggleRow({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-full text-sm border rounded-md py-1.5 ${
        value
          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
          : 'bg-muted border-border text-muted-foreground'
      }`}
    >
      {label}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hides children while the user is inside Street View (Pegman dropped).
// Street View takes over the entire map container with its own native UI,
// so any custom absolute-positioned overlay collides with it.
// ────────────────────────────────────────────────────────────────────────────
function StreetViewHide({ mapId, children }: { mapId: string; children: React.ReactNode }) {
  const map = useMap(mapId);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!map) return;
    const sv = map.getStreetView();
    const sync = () => setVisible(sv.getVisible());
    sync();
    const l = sv.addListener('visible_changed', sync);
    return () => l.remove();
  }, [map]);
  if (visible) return null;
  return <>{children}</>;
}

// ────────────────────────────────────────────────────────────────────────────
// Pop-up shown when an operator clicks a persisted geofence polygon.
// Renders an InfoWindow at the polygon's centroid with a quick summary
// and an "Editar" shortcut.
// ────────────────────────────────────────────────────────────────────────────
function GeofencePreview({
  mapId,
  row,
  onClose,
  onEdit,
}: {
  mapId: string;
  row: GeofenceRow;
  onClose: () => void;
  onEdit?: () => void;
}) {
  const map = useMap(mapId);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const closeListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const domListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    if (!map) return;
    const ring = row.polygon_geojson.coordinates[0] ?? [];
    if (!ring.length) return;
    const bounds = new google.maps.LatLngBounds();
    ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
    const center = bounds.getCenter();

    const container = document.createElement('div');
    container.style.minWidth = '180px';
    container.style.maxWidth = '240px';
    container.style.color = '#111827';
    const color = TYPE_COLORS[row.type];
    const typeLabel = GEOFENCE_TYPE_LABELS[row.type];
    const sevLabel = row.severity ? SEVERITY_LABELS[row.severity] : '';
    const hours = row.active_hours_start && row.active_hours_end
      ? `${trimTime(row.active_hours_start)}–${trimTime(row.active_hours_end)}`
      : '';
    container.innerHTML = `
      <div style="font:600 13px system-ui;margin-bottom:4px">${escapeHtml(row.name)}</div>
      <div style="display:flex;align-items:center;gap:6px;font:12px system-ui;margin-bottom:4px">
        <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color}"></span>
        <span>${escapeHtml(typeLabel)}</span>
        ${sevLabel ? `<span style=\"color:#6b7280\">· ${escapeHtml(sevLabel)}</span>` : ''}
      </div>
      ${row.category ? `<div style=\"font:12px system-ui;color:#4b5563;margin-bottom:4px\">${escapeHtml(row.category)}</div>` : ''}
      ${hours ? `<div style=\"font:12px system-ui;color:#4b5563;margin-bottom:4px\">Horario: ${hours}</div>` : ''}
      ${!row.is_active ? '<div style="font:12px system-ui;color:#dc2626;margin-bottom:4px">Inactiva</div>' : ''}
      ${onEdit ? '<button type="button" data-asi-edit="1" style="margin-top:4px;font:600 12px system-ui;color:#2563eb;background:none;border:none;padding:0;cursor:pointer">Editar geocerca \u2192</button>' : ''}
    `;
    const editBtn = container.querySelector('[data-asi-edit="1"]') as HTMLButtonElement | null;
    if (editBtn && onEdit) editBtn.addEventListener('click', onEdit);

    const iw = new google.maps.InfoWindow({ content: container, position: center });
    iw.open({ map });
    infoWindowRef.current = iw;
    closeListenerRef.current = iw.addListener('closeclick', onClose);

    return () => {
      closeListenerRef.current?.remove();
      closeListenerRef.current = null;
      domListenerRef.current?.remove();
      domListenerRef.current = null;
      if (editBtn && onEdit) editBtn.removeEventListener('click', onEdit);
      iw.close();
      infoWindowRef.current = null;
    };
  }, [map, row, onClose, onEdit]);

  return null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ────────────────────────────────────────────────────────────────────────────
// Compute the centroid (bounds center) of a geofence polygon for fly-to.
// ────────────────────────────────────────────────────────────────────────────
function polygonCentroid(row: GeofenceRow): { lat: number; lng: number } | null {
  const ring = row.polygon_geojson.coordinates[0] ?? [];
  if (!ring.length) return null;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const [lng, lat] of ring) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

// ────────────────────────────────────────────────────────────────────────────
// Fly-to animation when the operator selects a geofence.
// Mirrors the EmergencyMap MapController behaviour: position + zoom interpolate
// each rAF frame via moveCamera(), with zoom following a parabolic arc.
// ────────────────────────────────────────────────────────────────────────────
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function GeofenceFlyToController({
  mapId,
  target,
}: {
  mapId: string;
  target: { lat: number; lng: number } | null;
}) {
  const map = useMap(mapId);
  const rAFRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map || !target) return;
    const cancel = () => {
      if (rAFRef.current !== null) {
        cancelAnimationFrame(rAFRef.current);
        rAFRef.current = null;
      }
    };
    cancel();

    const startCenter = map.getCenter();
    if (!startCenter) return;
    const startLat = startCenter.lat();
    const startLng = startCenter.lng();
    const startZoom = map.getZoom() ?? 12;
    const targetZoom = 17;

    const dlat = target.lat - startLat;
    const dlng = target.lng - startLng;
    const dist = Math.sqrt(dlat * dlat + dlng * dlng);
    const midZoom = dist < 0.005
      ? (startZoom + targetZoom) / 2
      : Math.max(targetZoom - 4 - Math.log2(1 + dist * 40), 8);

    const DURATION_MS = 1800;
    const startTime = performance.now();

    const tick = (now: number) => {
      const raw = Math.min((now - startTime) / DURATION_MS, 1);
      const posT = easeInOutCubic(raw);
      const lat = startLat + (target.lat - startLat) * posT;
      const lng = startLng + (target.lng - startLng) * posT;
      let zoom: number;
      if (raw < 0.5) {
        zoom = startZoom + (midZoom - startZoom) * easeInOutCubic(raw * 2);
      } else {
        zoom = midZoom + (targetZoom - midZoom) * easeInOutCubic((raw - 0.5) * 2);
      }
      map.moveCamera({ center: { lat, lng }, zoom });
      if (raw < 1) {
        rAFRef.current = requestAnimationFrame(tick);
      } else {
        rAFRef.current = null;
      }
    };
    rAFRef.current = requestAnimationFrame(tick);
    return cancel;
    // Re-run when target lat/lng change. Including raw object reference is fine
    // because previewCenter is memoised in the parent.
  }, [map, target]);

  return null;
}
